import {
  Component,
  OnInit,
  Input,
  forwardRef,
  Output,
  EventEmitter,
  SimpleChanges,
  ElementRef,
  OnChanges,
} from '@angular/core';
// Modules
import {
  FormControl,
  Validators,
  ControlValueAccessor,
  NG_VALUE_ACCESSOR,
} from '@angular/forms';

// Interfaces
import { ICustomDropdown } from '@shared/interfaces';

// Custom validators
import { removeSpaces } from '@modules/shared/validators/removeSpaces';
@Component({
    selector: 'app-custom-suggested',
    templateUrl: './custom-suggested.component.html',
    styleUrls: ['./custom-suggested.component.scss'],
    host: {
        '(document:click)': 'onClick($event)',
    },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CustomSuggestedComponent),
            multi: true,
        },
    ],
    standalone: false
})
export class CustomSuggestedComponent
  implements ControlValueAccessor, OnInit, OnChanges
{
  // Outputs
  @Output() onValueChanged = new EventEmitter<any>();

  // Inputs
  @Input() options: ICustomDropdown[] = [];
  @Input() loading: boolean = false;
  @Input() hasError: boolean = false;
  @Input() placeholder: string = '';
  @Input() allowCustomValue: boolean = false;
  @Input() disabled: boolean = false;
  @Input() currentValue: ICustomDropdown;

  //Public variables
  public optionsAvailable: ICustomDropdown[] = [];
  public currentSelection: ICustomDropdown;
  public customOption: ICustomDropdown;
  public searchControl: FormControl = new FormControl(
    '',
    Validators.compose([Validators.required, removeSpaces])
  );
  public customOptionText: string = 'Utilizar: ';
  public datalistOpened: boolean = false;

  // Private variables
  private _onChange: (value: any) => void = () => {};
  private _onTouched = () => {};

  // Constructor
  constructor(private _eref: ElementRef) {}

  ngOnInit() {
    this.optionsAvailable = this.options;
  }

  // On component changes
  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.options) {
      this.optionsAvailable = this.options;
    }

    if (changes && changes.currentValue) {
      if (this.currentValue) {
        this.searchControl.setValue(this.currentValue.name);
      } else {
        this.searchControl.setValue(null);
      }
    }
    if (changes && changes.disabled) {
      if (this.disabled) {
        this.searchControl.disable({ emitEvent: false });
      } else {
        this.searchControl.enable({ emitEvent: false });
      }
    }
  }

  // Public method to store the value from the formGroup
  public writeValue(value: any) {
    this.currentSelection = value || null;
  }

  // Public method to register a form control change
  public registerOnChange(fn: any) {
    this._onChange = fn;
  }

  // Public method to register a form control touched
  public registerOnTouched(fn: any) {
    this._onTouched = fn;
  }

  // Public method to handle keypress
  public onSuggestedInputKeypress(event): void {
    const optionText = event.target.value.replace(this.customOptionText, '');
    this._setCustomOptionValue(event, optionText);
    this._setOptionsAvailable(event, optionText);
  }

  // Private method to set the custom option value
  private _setCustomOptionValue(event, optionText: string): void {
    if (this.allowCustomValue) {
      if (event.target.value.trim()) {
        this.customOption = {
          name: optionText,
          value: optionText,
        };
        this.onValueChanged.emit(this.customOption.name);
      } else {
        this.customOption = null;
      }
    }
  }

  // Private method to filter the options
  private _setOptionsAvailable(event, optionText: string) {
    if (event.target.value.trim()) {
      this.optionsAvailable = this.options.filter(el =>
        el.name.toLowerCase().includes(optionText.toLowerCase())
      );
    } else {
      this.optionsAvailable = this.options;
      this._onChange(null);
      this._onTouched();
    }
  }

  // Public function to watch when an option is selected
  public onSuggestedOptionSelected(option): void {
    this.currentSelection = option;
    this.searchControl.setValue(option.name);
    this._onChange(option);
    this._onTouched();
  }

  // Public method to handle the clicks out of the component
  public onClick(event): void {
    if (!this.disabled) {
      // Close all suggested expect the active one
      if (!this._eref.nativeElement.contains(event.target)) {
        const element = this._eref.nativeElement as HTMLElement;
        element.firstElementChild.classList.remove('opened');
        this.datalistOpened = false;
      }

      // Check if the active suggested should be opened or closed
      if (this._eref.nativeElement.contains(event.target)) {
        if (this.datalistOpened) {
          this.datalistOpened = false;
          this._onTouched();
        } else {
          this.datalistOpened = true;
        }
      }
    }
  }
}
